import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { learnMasterclassCompletionPath } from "@/routes"

/**
 * OKS-56 — Creator-side "Mark as complete" form.
 *
 * **Crew hard rule (P0):** Inertia `<Form>` from `@inertiajs/react`
 * with a plain shadcn `Button`. NO shadcn `Form`, NO `react-hook-form`,
 * NO `zod`. Request body is intentionally empty — the URL identifies
 * the row, the server reads no body params (TRD KD-5).
 *
 * `processing` render-prop swaps the button label from "Mark as
 * complete" → spinner + "Marking…" (UX Spec §4). `errors` render-prop
 * is unused — there's no validation surface (the server `find_or_create_by!`
 * is idempotent; a 5xx surfaces via a flash error toast handled
 * elsewhere).
 *
 * Layout positioning (mobile sticky / tablet inline) is owned by the
 * parent Show page, not this component — it just renders the button.
 */
export interface CreatorMasterclassMarkCompleteFormProps {
  masterclassId: number
  /**
   * Optional className passthrough so the parent can stack the form
   * inside a sticky-bottom wrapper on mobile + inline wrapper on
   * tablet+.
   */
  className?: string
}

export function CreatorMasterclassMarkCompleteForm({
  masterclassId,
  className,
}: CreatorMasterclassMarkCompleteFormProps) {
  const { t } = useTranslation()

  return (
    <Form
      action={learnMasterclassCompletionPath(masterclassId)}
      method="post"
      className={className}
      data-testid="learn-mark-complete-form"
    >
      {({ processing }) => (
        <Button
          type="submit"
          size="lg"
          disabled={processing}
          className="w-full sm:w-auto"
          data-testid="learn-mark-complete-button"
        >
          {processing ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t("learn.show.action.marking")}
            </>
          ) : (
            t("learn.show.action.mark_complete")
          )}
        </Button>
      )}
    </Form>
  )
}
